import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { Flame, Square } from 'lucide-react';
import type { BatchDetail } from '@autopilot/contracts';
import { api } from '../../api/client';
import { qk, useCatalogs, useRoles } from '../../api/hooks';
import { isConflict } from '../../api/errors';
import { ErrorAlert } from '../../components/States';
import { TokenValue } from '../../components/TokenValue';
import { useIdempotencyKey } from '../../lib/idempotency';
import { PROVIDER_LABEL } from '../../lib/labels';
import { modelUsable } from '../../lib/models';

type GenModel = { id: string; name: string; provider: string; modelId: string };

/** Aktive Generatormodelle: aus /catalogs (falls geliefert), sonst Admin-Modellliste. */
function useGeneratorModels() {
  const catalogs = useCatalogs();
  const { isAdmin } = useRoles();
  const fromCatalog = catalogs.data?.generatorModels;
  const admin = useQuery({
    queryKey: qk.models,
    queryFn: api.listModels,
    enabled: !fromCatalog && isAdmin && !catalogs.isPending,
  });
  const list: GenModel[] | undefined = fromCatalog
    ? fromCatalog.filter((m) => m.active !== false && (m.roles ?? ['GENERATOR']).includes('GENERATOR'))
    : admin.data?.filter((m) => modelUsable(m) && m.roles.includes('GENERATOR'));
  return { list, loading: catalogs.isPending || admin.isFetching };
}

export function PreheatConfig({ batch }: { batch: BatchDetail }) {
  const qc = useQueryClient();
  const { isOperator } = useRoles();
  const catalogs = useCatalogs();
  const models = useGeneratorModels();
  const [modelId, setModelId] = useState('');
  const idem = useIdempotencyKey();
  useEffect(() => {
    if (!modelId && models.list?.length === 1) setModelId(models.list[0]!.id);
  }, [models.list, modelId]);
  const reviewProfile = batch.reviewProfile ?? catalogs.data?.reviewProfile ?? null;
  const usable = batch.selection?.counts?.usable ?? 0;

  const start = useMutation({
    mutationFn: () => {
      const body = { expectedRowVersion: batch.rowVersion, generatorModelConfigId: modelId };
      return api.preheat(batch.id, body, idem.keyFor({ id: batch.id, ...body }));
    },
    onSuccess: (b) => {
      idem.reset();
      qc.setQueryData(qk.batch(batch.id), b);
    },
    onError: (err) => {
      if (isConflict(err)) void qc.invalidateQueries({ queryKey: qk.batch(batch.id) });
    },
  });

  const noModels = !models.loading && (!models.list || models.list.length === 0);
  let disabledReason: string | null = null;
  if (!isOperator) disabledReason = 'Nur Operatoren können Pre-Heating starten.';
  else if (usable === 0) disabledReason = 'Keine nutzbaren Tickets.';
  else if (noModels) disabledReason = 'Kein aktives Generatormodell konfiguriert – ein Admin muss unter Administration → Modelle eines aktivieren.';
  else if (!reviewProfile) disabledReason = 'Kein aktives Prüfprofil konfiguriert – ein Admin muss unter Administration → Prüfprofile eines anlegen.';
  else if (!modelId) disabledReason = 'Bitte ein Generatormodell wählen.';

  return (
    <Card component="section" aria-labelledby="preheat-title">
      <CardContent sx={{ p: 3 }}>
        <Typography id="preheat-title" variant="h5" component="h2">
          Pre-Heating
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 2.5 }}>
          Für jedes nutzbare Ticket erstellt das Generatormodell einen Antwortvorschlag; zwei unabhängige Prüfer bewerten Richtigkeit und Fehlerrisiko. Es wird nichts versendet.
        </Typography>
        <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
          <FormControl fullWidth size="small" disabled={!isOperator || noModels || start.isPending}>
            <InputLabel id="gen-model-label">Generatormodell</InputLabel>
            <Select
              labelId="gen-model-label"
              label="Generatormodell"
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              data-testid="generator-select"
            >
              {(models.list ?? []).map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name} · {PROVIDER_LABEL[m.provider as keyof typeof PROVIDER_LABEL] ?? m.provider} ({m.modelId})
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Nur aktive Modelle mit hinterlegtem Zugang. Kein automatischer Ersatz.</FormHelperText>
          </FormControl>
          <Box>
            <Typography variant="subtitle2">Prüfprofil (durch Admin festgelegt)</Typography>
            {reviewProfile ? (
              <Box data-testid="review-profile">
                <Typography variant="body2">
                  {reviewProfile.name} · Version {reviewProfile.version}
                </Typography>
                {batch.reviewProfile && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Richtigkeit: {batch.reviewProfile.correctness.name} ({batch.reviewProfile.correctness.modelId}) · Fehlerrisiko: {batch.reviewProfile.errorRisk.name} ({batch.reviewProfile.errorRisk.modelId})
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Kein aktives Prüfprofil.
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={start.isPending ? <CircularProgress size={16} color="inherit" /> : <Flame size={18} />}
            disabled={!!disabledReason || start.isPending}
            onClick={() => start.mutate()}
            data-testid="start-preheat"
            aria-describedby={disabledReason ? 'preheat-disabled' : undefined}
          >
            Start Pre-Heating ({usable.toLocaleString('de-DE')} Tickets)
          </Button>
          {disabledReason && (
            <Typography id="preheat-disabled" variant="body2" sx={{ color: 'text.secondary' }}>
              {disabledReason}
            </Typography>
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          <ErrorAlert error={start.error} action="Pre-Heating starten" onClose={() => start.reset()} />
        </Box>
      </CardContent>
    </Card>
  );
}

export function PreheatProgress({ batch, onStop, stopping }: { batch: BatchDetail; onStop: () => void; stopping: boolean }) {
  const { isOperator } = useRoles();
  const p = batch.progress;
  const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
  return (
    <Card component="section" aria-labelledby="preheat-progress-title">
      <CardContent sx={{ p: 3 }}>
        <Typography id="preheat-progress-title" variant="h5" component="h2">
          Pre-Heating läuft
        </Typography>
        <Typography variant="body1" sx={{ mt: 1.5, fontWeight: 600 }} data-testid="preheat-progress" aria-live="polite">
          {p.done.toLocaleString('de-DE')} von {p.total.toLocaleString('de-DE')} fertig, davon {p.failed.toLocaleString('de-DE')} mit Fehler
        </Typography>
        <LinearProgress variant="determinate" value={pct} sx={{ my: 1.5, height: 8, borderRadius: 4 }} aria-label={`Fortschritt ${pct} Prozent`} />
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', color: 'text.secondary' }}>
          <Typography variant="body2">
            Phase: {p.inProgress > 0 ? `Vorschläge und Prüfungen (${p.inProgress} in Bearbeitung)` : 'Vorbereitung'}
          </Typography>
          <Typography variant="body2" component="div">
            Gemeldete Tokens: <TokenValue usage={batch.usage} />
          </Typography>
          {batch.generator && <Typography variant="body2">Modell: {batch.generator.name}</Typography>}
        </Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
          Einzelne Fehler stoppen den Batch nicht. Sie können die Seite schließen – die Verarbeitung läuft auf dem Server weiter.
        </Typography>
        {isOperator && (
          <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<Square size={16} />} onClick={onStop} disabled={stopping} data-testid="pause-preheat">
              Pre-Heating anhalten
            </Button>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Hält den Batch an; er kann später fortgesetzt oder abgebrochen werden.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
